import React from "react";

const site = typeof window !== "undefined" ? window.location.origin : "https://fablelot.com";
const example = `curl -X POST ${site}/api/thistleglass/agent/v1/listings \\
  -H "Authorization: Bearer $FABLELOT_AGENT_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: mug-sea-blue-001" \\
  -d '{
    "title": "Speckled stoneware mug, sea-blue drip glaze",
    "description": "Wheel-thrown stoneware, holds about 12 oz. Dishwasher safe.",
    "price": { "amount": "28.00", "currency": "USD" },
    "category": "ceramics-and-pottery",
    "kind": "handmade",
    "sku": "HA-MUG-SEA",
    "tags": ["mug", "stoneware", "gift"],
    "images": [{ "data": "<base64 JPEG/PNG/WebP>" }]
  }'`;

/** Public guide for people building AI agents that suggest listings. */
export function DeveloperGuide() {
  const row = (method: string, path: string, what: string) => (
    <tr><td><code>{method}</code></td><td><code>{path}</code></td><td>{what}</td></tr>
  );
  return (
    <section className="developer-guide" style={{ maxWidth: "72ch" }}>
      <h2>Build an agent that lists on Fablelot</h2>
      <p>AI agents can suggest listings for a shop. A suggestion is never published automatically: the shop owner reviews it, turns it into a draft, adds photos and confirms publication. This keeps every shop in its owner’s hands.</p>
      <h3>1. Get a key</h3>
      <p>The shop owner opens <strong>Seller studio → Listings → From your AI agents → Agent keys</strong>, names the agent and creates a key. The key starts with <code>fla_</code> and is shown once. It can only send suggestions to that one shop and can be revoked at any time.</p>
      <h3>2. Call the API</h3>
      <p>Send the key as a bearer token. All requests and responses are JSON.</p>
      <div style={{ overflowX: "auto" }}>
        <table>
          <thead><tr><th>Method</th><th>Path</th><th>What it does</th></tr></thead>
          <tbody>
            {row("GET", "/api/thistleglass/agent/v1/me", "Your agent name, shop, the categories you can use and size limits.")}
            {row("POST", "/api/thistleglass/agent/v1/listings", "Suggest a listing. Returns its id with status “pending”.")}
            {row("GET", "/api/thistleglass/agent/v1/listings", "Your last 100 suggestions and whether the owner used or dismissed them.")}
          </tbody>
        </table>
      </div>
      <h3>3. Suggest a listing</h3>
      <pre style={{ overflowX: "auto", whiteSpace: "pre", fontSize: 14 }}><code>{example}</code></pre>
      <ul>
        <li><strong>Required:</strong> <code>title</code> (up to 140 characters), <code>description</code> (up to 4,000), <code>price</code> (<code>amount</code> as a decimal string, <code>currency</code> as a 3-letter code) and <code>category</code> (one of the ids from <code>/me</code>).</li>
        <li><strong>Optional:</strong> <code>sku</code>, <code>kind</code> (handmade, designed, vintage or supply), <code>condition</code>, <code>origin</code>, <code>process</code>, up to 13 <code>tags</code>, and up to 4 <code>images</code> (JPEG, PNG or WebP, 5 MB each, base64).</li>
        <li>Send an <code>Idempotency-Key</code> header to retry safely: the same key returns the original suggestion instead of creating a copy.</li>
        <li>Every image is virus-scanned before the owner can see it.</li>
      </ul>
      <h3>Errors</h3>
      <p><code>401 AGENT_KEY_INVALID</code>: the key is wrong or revoked. <code>400 INVALID_LISTING</code>: a field is missing or not allowed (the message names it). <code>429 TOO_MANY_PENDING</code>: the owner has 500 suggestions waiting; try again later.</p>
      <h3>What agents can’t do</h3>
      <p>Agent keys can’t publish, change prices of live listings, buy, message buyers or see orders and personal data. Those stay with people.</p>
    </section>
  );
}
